import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle2,
  Clock,
  Copy,
  FileDown,
} from "lucide-react";
import api from "@/api/client";
import DeliveryMap from "@/components/DeliveryMap";
import { toast } from "sonner";

const STAGES = [
  { key: "placed", label: "Order Placed", desc: "Payment received, order confirmed", icon: CheckCircle2 },
  { key: "packed", label: "Packed & Sealed", desc: "Quality checked, batch tagged", icon: Package },
  { key: "out_for_delivery", label: "Out for Delivery", desc: "Truck dispatched from warehouse", icon: Truck },
  { key: "delivered", label: "Delivered", desc: "Signed & received at site", icon: CheckCircle2 },
];

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (alive) setOrder(data);
      } catch {
        toast.error("Order not found");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 3000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [id]);

  if (loading || !order) {
    return (
      <div className="min-h-screen bg-zinc-100 p-10">
        <div className="h-96 bg-zinc-200 animate-pulse" />
      </div>
    );
  }

  const t = order.tracking;
  const currentIndex = STAGES.findIndex((s) => s.key === t.current_status);

  const downloadInvoice = async () => {
    try {
      const token = localStorage.getItem("rk_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to fetch invoice");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `udyog-invoice-${order.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="px-6 lg:px-10 py-6">
        <Link
          to="/orders"
          className="inline-flex items-center gap-2 font-mono-data text-xs tracking-widest hover:text-[#FF5A00]"
        >
          <ArrowLeft className="w-4 h-4" /> ALL ORDERS
        </Link>
      </div>

      <div className="px-6 lg:px-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="tag">ORDER · {order.id.slice(0, 8).toUpperCase()}</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
              {t.current_status === "delivered" ? "Delivered ✓" : `ETA ${t.eta_display}`}
            </h1>
            <p className="text-zinc-500 mt-2 font-mono-data text-sm">
              Placed on {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(order.id);
              toast.success("Order ID copied");
            }}
            data-testid="copy-order-id"
            className="inline-flex items-center gap-2 border-2 border-zinc-900 px-4 py-2 font-mono-data text-xs tracking-widest hover:bg-zinc-900 hover:text-white"
          >
            <Copy className="w-4 h-4" /> COPY ID
          </button>
          {order.payment_status === "paid" && (
            <button
              onClick={downloadInvoice}
              data-testid="download-invoice-button"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white border-2 border-zinc-900 px-4 py-2 font-mono-data text-xs tracking-widest hard-shadow-sm ml-2"
            >
              <FileDown className="w-4 h-4" /> INVOICE PDF
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-8 bg-white border-2 border-zinc-900 p-4">
          <div className="h-3 bg-zinc-200 relative">
            <div
              className="absolute inset-y-0 left-0 bg-[#FF5A00] transition-all duration-500"
              style={{ width: `${t.progress_pct}%` }}
              data-testid="progress-bar"
            />
          </div>
          <div className="mt-2 flex justify-between font-mono-data text-xs">
            <span>{t.progress_pct}% complete</span>
            <span>ETA {t.eta_display}</span>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-5 gap-4">
          {/* Timeline */}
          <div className="lg:col-span-2 bg-white border border-zinc-900 p-6">
            <div className="tag mb-4">DELIVERY TIMELINE</div>
            <ol className="space-y-6" data-testid="timeline">
              {STAGES.map((s, i) => {
                const stage = t.timeline.find((x) => x.status === s.key) || {};
                const reached = stage.reached;
                const active = i === currentIndex && t.current_status !== "delivered";
                const Icon = s.icon;
                return (
                  <li key={s.key} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 flex items-center justify-center border-2 ${
                          reached
                            ? "bg-[#FF5A00] border-zinc-900 text-white"
                            : "bg-zinc-100 border-zinc-300 text-zinc-400"
                        } ${active ? "ring-4 ring-[#FF5A00]/30" : ""}`}
                        data-testid={`timeline-${s.key}`}
                      >
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      {i < STAGES.length - 1 && (
                        <div
                          className={`w-0.5 flex-1 min-h-[24px] ${
                            reached ? "bg-[#FF5A00]" : "bg-zinc-300"
                          }`}
                        />
                      )}
                    </div>
                    <div className="pb-4 flex-1">
                      <div
                        className={`font-display font-bold text-lg tracking-tight ${
                          reached ? "text-zinc-900" : "text-zinc-400"
                        }`}
                      >
                        {s.label}
                      </div>
                      <div className={`text-sm ${reached ? "text-zinc-600" : "text-zinc-400"}`}>
                        {s.desc}
                      </div>
                      {stage.at && (
                        <div className="text-xs font-mono-data text-zinc-500 mt-1">
                          {reached
                            ? new Date(stage.at).toLocaleString()
                            : "Expected " + new Date(stage.at).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Map */}
          <div className="lg:col-span-3 min-h-[500px] flex flex-col">
            <div className="tag mb-2">LIVE DRIVER LOCATION</div>
            <div className="flex-1 min-h-[400px]">
              <DeliveryMap tracking={t} />
            </div>
            <div className="mt-3 bg-white border border-zinc-900 p-4 flex flex-wrap gap-4 items-center justify-between font-mono-data text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-zinc-900" />
                <span>WAREHOUSE (MUMBAI)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#FF5A00] rotate-45" />
                <span>DRIVER · LIVE</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-600 rounded-full" />
                <span>YOUR FACTORY</span>
              </div>
            </div>
          </div>
        </div>

        {/* Order items */}
        <div className="mt-6 bg-white border border-zinc-900 p-6">
          <div className="tag mb-4">SHIPPING</div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="font-display font-bold text-lg">Delivery address</div>
              <div className="text-zinc-600 mt-1">{order.address}</div>
              <div className="text-zinc-500 mt-1 text-sm">{order.delivery_city}</div>
            </div>
            <div>
              <div className="font-display font-bold text-lg">Payment</div>
              <div className="text-zinc-600 mt-1 font-mono-data text-sm">
                {order.razorpay_payment_id || "—"}
              </div>
              <div className="text-zinc-500 mt-1 text-sm capitalize">
                Status: {order.payment_status}
              </div>
            </div>
          </div>

          <div className="tag mt-8 mb-3">ITEMS ({order.items.length})</div>
          <div className="space-y-2">
            {order.items.map((it) => (
              <div
                key={it.product_id}
                className="flex items-center gap-4 border border-zinc-200 p-3"
              >
                <div className="w-14 h-14 border border-zinc-300 overflow-hidden shrink-0">
                  <img src={it.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="tag">{it.sku}</div>
                  <div className="font-medium leading-tight mt-0.5">{it.name}</div>
                  <div className="text-xs font-mono-data text-zinc-500">
                    {it.quantity} {it.unit} × ₹{it.price.toFixed(2)}
                  </div>
                </div>
                <div className="font-mono-data font-bold">
                  ₹{it.line_total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-dashed border-zinc-300 pt-4 font-mono-data text-sm">
            <div>
              <div className="tag">Subtotal</div>
              <div className="mt-1">₹{order.subtotal.toFixed(2)}</div>
            </div>
            <div>
              <div className="tag">GST (18%)</div>
              <div className="mt-1">₹{order.tax.toFixed(2)}</div>
            </div>
            <div>
              <div className="tag">Delivery</div>
              <div className="mt-1">
                {order.delivery_fee === 0 ? "FREE" : `₹${order.delivery_fee.toFixed(2)}`}
              </div>
            </div>
            <div>
              <div className="tag">Total paid</div>
              <div className="mt-1 font-bold text-lg">
                ₹{order.total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
